import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  autoChangeListQuery,
  autoChangeShellQuery,
  autoChangeTypeStatusesQuery,
  changeAutoChangeRuleStatus,
  createAutoChangeRule,
  nextStatusesQuery,
  rebuildAutoChangeCron,
  removeAutoChangeRule,
  type AutoChangeRule,
  type AutoChangeShell,
} from '../../api/settingsEaseedeskAutoChangeStatus'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Scheduled change status.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * ticket_auto_change_status/index.php` + `list.php`, si gest cu gest fata de
 * cele doua fisiere din `public/js/views/.../ticket_auto_change_status/`.
 *
 * O regula muta tichetul dupa un TIMP: din starea A in starea B, dupa atatea
 * minute / ore / zile / luni. Cronul e cel care lucreaza, si de-aia exista
 * butonul "Refresh cron" - fara el, o regula noua nu incepe.
 *
 * "To status" nu e orice stare: sunt cele la care duce FLUXUL tipului din
 * starea de plecare (`get_workflow_next_status`), si cele ascunse nu se ofera.
 */
function RuleRow({ rule, onReload }: { rule: AutoChangeRule; onReload(): void }) {
  const flip = useMutation({
    mutationFn: (on: boolean) => changeAutoChangeRuleStatus(rule.ID, on),
    onSuccess: () => {
      toast('Setting changed', 'Success')
      onReload()
    },
    onError: () => {
      toast('Something went wrong.', 'Error', 'error')
      onReload()
    },
  })

  const remove = useMutation({
    mutationFn: () => removeAutoChangeRule(rule.ID),
    onSuccess: () => {
      toast('Setting deleted', 'Success')
      onReload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  return (
    <tr>
      <td>
        <span className="badge pull-left" style={{ minWidth: 23, backgroundColor: rule.color_identifier, color: '#fff' }}>
          {rule.short_name}
        </span>
      </td>
      <td>{rule.type_name}</td>
      <td>{rule.status_from_name}</td>
      <td>{rule.status_to_name}</td>
      <td>
        <span className="label label-primary">
          {rule.pending_time} {rule.pending_type}
        </span>
      </td>
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          name="active"
          checked={rule.active === 1}
          onChange={(e) => flip.mutate(e.target.checked)}
        />
      </td>
      <td style={{ textAlign: 'center' }}>
        <div className="btn-group" role="group" aria-label="controls">
          <button
            type="button"
            className="btn btn-danger btn-xs delete-setting"
            data-setting={rule.ID}
            onClick={() => {
              if (!window.confirm('Delete this setting?\nYou will not be able to recover this setting!')) return
              remove.mutate()
            }}
          >
            <i className="fas fa-trash" aria-hidden="true"></i>
          </button>
        </div>
      </td>
    </tr>
  )
}

export function AutoChangeStatusPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(autoChangeShellQuery())
  const list = useQuery(autoChangeListQuery())
  const [creating, setCreating] = useState(false)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'auto_change_status'] })
  }

  const rebuild = useMutation({
    mutationFn: () => rebuildAutoChangeCron(),
    onSuccess: (answer) =>
      answer.cron === 'failed'
        ? toast(answer.cron_message || 'The cron server did not answer.', 'Error', 'error')
        : toast('Cron refreshed', 'Success'),
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed error={shell.error} onRetry={() => void shell.refetch()} what="This screen" />
  }
  if (!shell.data) return <Spinner />

  return (
    <div>
      <h4>
        <strong>Scheduled</strong> change status
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage changing statuses <strong>automatically</strong>
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Add settings
      </button>
      <hr />

      <section className="settings">
        <div className="row">
          <div className="col-md-12">
            <div className="form-group row">
              <div className="col-md-12">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={rebuild.isPending}
                  onClick={() => rebuild.mutate()}
                >
                  <i className="fas fa-sync-alt" aria-hidden="true"></i> Refresh cron
                </button>
                <span className="help-block">Refresh configuration cron after every change.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="list-group">
        {list.error ? (
          <LoadFailed error={list.error} onRetry={() => void list.refetch()} what="The list" />
        ) : !list.data ? (
          <Spinner />
        ) : (
          <table className="table table-hover">
            <thead>
              <tr>
                <th width={60}></th>
                <th style={{ textAlign: 'left' }}>Ticket type</th>
                <th style={{ textAlign: 'left' }}>Status from</th>
                <th style={{ textAlign: 'left' }}>Status to</th>
                <th style={{ textAlign: 'left' }}>Pending time</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.data.settings.map((rule) => (
                <RuleRow key={rule.ID} rule={rule} onReload={reload} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AutoChangeModal open={creating} shell={shell.data} onClose={() => setCreating(false)} onSaved={reload} />
    </div>
  )
}

/** `newSettingsModal` din macheta: tip, de la, la, cat, si unitatea. */
export function AutoChangeModal({
  open,
  shell,
  onClose,
  onSaved,
}: {
  open: boolean
  shell: AutoChangeShell
  onClose(): void
  onSaved(): void
}) {
  const [type, setType] = useState(0)
  const [from, setFrom] = useState(0)
  const [to, setTo] = useState(0)
  const [time, setTime] = useState(1)
  const [unit, setUnit] = useState(shell.pending_types[0] ?? 'minutes')

  useEffect(() => {
    if (!open) return
    setType(0)
    setFrom(0)
    setTo(0)
    setTime(1)
    setUnit(shell.pending_types[0] ?? 'minutes')
  }, [open, shell.pending_types])

  /* Cand se schimba tipul sau starea de plecare, "To status" se goleste si se
     cere din nou - fluxul altui tip duce in alta parte. */
  const next = useQuery(nextStatusesQuery(type, from))
  useEffect(() => {
    setTo(0)
  }, [type, from])
  /* "From status" vine din fluxul tipului ales si se cere din nou la fiecare
     schimbare a lui, ca `getTicketStatus()` din vechea macheta. */
  const typeStatuses = useQuery(autoChangeTypeStatusesQuery(type))
  const fromStatuses = type > 0 ? (typeStatuses.data?.statuses ?? []) : []

  const save = useMutation({
    mutationFn: () => createAutoChangeRule(type, from, to, time, unit),
    onSuccess: () => {
      toast('Setting created', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  const incomplete = type < 1 || from < 1 || to < 1 || time < 1

  return (
    <Modal id="newSettingsModal" labelledBy="newSettingsModalLabel" open={open} onClose={onClose} title="New settings">
      <form
        id="new-settings-form"
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (incomplete) return
          save.mutate()
        }}
      >
        <div className="form-group">
          <label htmlFor="acs-type" className="control-label">
            Ticket type:
          </label>
          <select
            className="form-control"
            id="acs-type"
            name="ticket_type"
            value={type}
            onChange={(e) => {
              setType(Number(e.target.value))
              setFrom(0)
            }}
            required
          >
            <option value={0}>Select</option>
            {shell.ticketTypes.map((t) => (
              <option key={t.ID} value={t.ID}>
                {t.type_name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="acs-from" className="control-label">
            From status:
          </label>
          <select
            className="form-control"
            id="acs-from"
            name="status_from"
            value={from}
            onChange={(e) => setFrom(Number(e.target.value))}
            required
          >
            <option value={0}>Select</option>
            {fromStatuses.map((s) => (
              <option key={s.ID} value={s.ID}>
                {s.status_name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="acs-to" className="control-label">
            To status:
          </label>
          <select
            className="form-control"
            id="acs-to"
            name="status_to"
            value={to}
            disabled={type < 1 || from < 1}
            onChange={(e) => setTo(Number(e.target.value))}
            required
          >
            <option value={0}>Select</option>
            {(next.data?.statuses ?? []).map((s) => (
              <option key={s.ID} value={s.ID}>
                {s.status_name}
              </option>
            ))}
          </select>
          {type > 0 && from > 0 && next.data && next.data.statuses.length === 0 && (
            <span className="help-block">The flow of this ticket type does not leave that status.</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="pending-time" className="control-label">
            Pending time:
          </label>
          <input
            type="number"
            className="form-control"
            id="pending-time"
            name="pending_time"
            min={1}
            value={time}
            onChange={(e) => setTime(Number(e.target.value))}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="pending-type" className="control-label">
            Pending type:
          </label>
          <select
            className="form-control"
            id="pending-type"
            name="pending_type"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            required
          >
            {shell.pending_types.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className="btn btn-primary submit-new-settings"
            disabled={incomplete || save.isPending}
          >
            Create
          </button>
        </div>
      </form>
    </Modal>
  )
}
